import React from "react";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ReactMarkdown from "react-markdown";
import {
  API_get_labor_model_assistant_session,
  API_get_labor_standard_optimization_runs,
  API_labor_model_assistant_chat,
  API_reset_labor_model_assistant_session,
  API_resolve_labor_model_assistant_proposal,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import { buildLaborModelAssistantEvaluationContext } from "./laborModelAssistantEvaluation";

const SANDBOX_INITIAL_PROMPTS = [
  "Review my current labor model",
  "How is labor calculated?",
  "What is the difference between service and support labor?",
  "Which standards drive the most labor?",
  "Explain what changed from published",
  "Which stores have custom adjustments?",
  "Are my labor standards ready for recommendations?",
  "Refit this labor model using historical data",
];

const SANDBOX_SETUP_PROMPTS = [
  "What should I configure first in this sandbox?",
  "How do I set up labor drivers?",
  "How do I set labor standards?",
  "How should I set store-level adjustments?",
];

const SANDBOX_FOLLOW_UP_PROMPTS = [
  { label: "Review model", prompt: SANDBOX_INITIAL_PROMPTS[0] },
  { label: "Calculation", prompt: SANDBOX_INITIAL_PROMPTS[1] },
  { label: "Service vs. support", prompt: SANDBOX_INITIAL_PROMPTS[2] },
  { label: "Top standards", prompt: SANDBOX_INITIAL_PROMPTS[3] },
  { label: "Explain changes", prompt: SANDBOX_INITIAL_PROMPTS[4] },
  { label: "Store adjustments", prompt: SANDBOX_INITIAL_PROMPTS[5] },
];

const SANDBOX_IMPACT_PROMPTS = [
  { label: "Refresh impact", prompt: "Help me understand the latest impact results" },
  { label: "Published comparison", prompt: SANDBOX_INITIAL_PROMPTS[4] },
];

const PUBLISHED_QUICK_PROMPTS = [
  "Review this published labor model",
  "How does this model calculate labor?",
  "What is the difference between service and support labor?",
  "Which standards drive the most labor?",
  "Explain this model's store adjustments",
  "How is this different from the current published model?",
];

const sectionTone = {
  added: "bg-emerald-50 text-emerald-700",
  modified: "bg-amber-50 text-amber-700",
  removed: "bg-rose-50 text-rose-700",
};

const sectionLabels = [
  {
    pattern:
      /sales driver|attribute driver|program flag|time window|routed-sales/i,
    label: "Labor Drivers",
  },
  { pattern: /standard/i, label: "Labor Standards" },
  {
    pattern: /location|store|fit factor|labor budget/i,
    label: "Store Adjustments",
  },
];

function proposalSectionLabel(change) {
  const matchingSection = sectionLabels.find(({ pattern }) =>
    pattern.test(`${change.title || ""} ${(change.details || []).join(" ")}`)
  );
  return matchingSection?.label || "Labor Model";
}

function parseProposalDetail(detail) {
  const match = String(detail).match(/^([^:]+):\s*(.*?)\s+→\s+(.*)$/);
  return match
    ? { label: match[1], previous: match[2], next: match[3] }
    : null;
}

function buildInitialActionPills({
  readOnly,
  setupSummary,
  scenarioChangeCount,
  scenarioChangeCountLoading,
  evaluationFresh,
  optimizationRun,
  messagesCount,
}) {
  const pills = [];
  const safeSummary = setupSummary || {};
  const driverCoverage =
    Number(safeSummary.salesDrivers || 0) +
    Number(safeSummary.attributes || 0) +
    Number(safeSummary.flags || 0);
  const hasDrivers = driverCoverage > 0;
  const hasStandards = Number(safeSummary.standards || 0) > 0;
  const hasStoreCustomization = Number(safeSummary.adjustments || 0) > 0;
  const optimizationInFlight = ["queued", "running"].includes(
    String(optimizationRun?.status || "").toLowerCase()
  );

  const addPill = (label, prompt, disabledReason = null) => {
    if (pills.some((current) => current.prompt === prompt)) return;
    pills.push({
      label,
      prompt,
      disabledReason,
    });
  };

  if (readOnly) {
    PUBLISHED_QUICK_PROMPTS.forEach((prompt) => {
      addPill(prompt, prompt, null);
    });
    return pills.slice(0, 6);
  }

  if (messagesCount > 0) {
    if (!evaluationFresh) {
      addPill(
        "Refresh impact",
        SANDBOX_IMPACT_PROMPTS[0].prompt,
        "Impact is currently stale"
      );
    }
    if (!hasDrivers) {
      addPill("Start with labor drivers", SANDBOX_SETUP_PROMPTS[0]);
      addPill("Build labor drivers", SANDBOX_SETUP_PROMPTS[1]);
    }
    if (!hasStandards) {
      addPill("Set up labor standards", SANDBOX_SETUP_PROMPTS[2]);
    }
    if (!hasStoreCustomization) {
      addPill("Review store adjustments", SANDBOX_SETUP_PROMPTS[3]);
    }
    if (!optimizationInFlight && hasStandards) {
      addPill("Recommend standards", SANDBOX_INITIAL_PROMPTS[7]);
    }
    return pills.slice(0, 6);
  }

  if (driverCoverage === 0) {
    addPill("Get started", SANDBOX_SETUP_PROMPTS[0]);
    addPill("Set up labor drivers", SANDBOX_SETUP_PROMPTS[1]);
    addPill("Set up labor standards", SANDBOX_SETUP_PROMPTS[2]);
  }

  if (driverCoverage > 0 && !hasStandards) {
    addPill("What are labor standards?", SANDBOX_SETUP_PROMPTS[2]);
    addPill("Recommend standards", SANDBOX_INITIAL_PROMPTS[7]);
  }

  if (hasStandards && !hasStoreCustomization) {
    addPill("Review store adjustments", SANDBOX_SETUP_PROMPTS[3]);
  }

  if (!evaluationFresh) {
    addPill(
      "Refresh impact",
      SANDBOX_IMPACT_PROMPTS[0].prompt,
      "Impact is currently stale"
    );
  }

  if (!scenarioChangeCountLoading && scenarioChangeCount === 0) {
    addPill(
      "Review with published",
      SANDBOX_IMPACT_PROMPTS[1].prompt,
      null
    );
  }

  if (optimizationInFlight) {
    addPill("Check recommendation", SANDBOX_IMPACT_PROMPTS[0].prompt, "Recommendations are still running");
  } else if (hasStandards) {
    addPill("Recommend standards", SANDBOX_INITIAL_PROMPTS[7]);
  }

  if (pills.length === 0) {
    SANDBOX_INITIAL_PROMPTS.forEach((prompt) => {
      const fallbackLabel = prompt.split("?")[0].slice(0, 28);
      addPill(fallbackLabel, prompt);
    });
  }

  return pills
    .filter((pill) => !pill.disabledReason)
    .concat(pills.filter((pill) => Boolean(pill.disabledReason)))
    .slice(0, 8);
}

function buildFollowUpActionPills({
  readOnly,
  scenarioChangeCount,
  setupSummary,
  optimizationRun,
  evaluationFresh,
  proposal,
  messages,
}) {
  const safeSummary = setupSummary || {};
  const driverCoverage =
    Number(safeSummary.salesDrivers || 0) +
    Number(safeSummary.attributes || 0) +
    Number(safeSummary.flags || 0);
  const hasDrivers = driverCoverage > 0;
  const hasStandards = Number(safeSummary.standards || 0) > 0;
  const optimizationInFlight = ["queued", "running"].includes(
    String(optimizationRun?.status || "").toLowerCase()
  );

  if (readOnly) {
    return [
      { label: "Review model", prompt: PUBLISHED_QUICK_PROMPTS[0] },
      { label: "Calculation", prompt: PUBLISHED_QUICK_PROMPTS[1] },
      { label: "Service vs. support", prompt: PUBLISHED_QUICK_PROMPTS[2] },
      { label: "Store adjustments", prompt: PUBLISHED_QUICK_PROMPTS[4] },
    ];
  }

  if (proposal) return [];

  const options = [];
  if (!evaluationFresh) {
    options.push({
      label: "Refresh impact",
      prompt: SANDBOX_IMPACT_PROMPTS[0].prompt,
      disabledReason: "Impact context is stale",
    });
  }

  if (!hasDrivers) {
    options.push({
      label: "Build labor drivers",
      prompt: "How do I set up labor drivers?",
    });
  }

  if (!hasStandards) {
    options.push({
      label: "Understand labor standards",
      prompt: "How do labor standards work and affect my forecast?",
    });
  }

  if (messages.length > 2 || (scenarioChangeCount ?? 0) > 0) {
    options.push(
      SANDBOX_FOLLOW_UP_PROMPTS[0],
      SANDBOX_FOLLOW_UP_PROMPTS[1],
      SANDBOX_FOLLOW_UP_PROMPTS[2],
      SANDBOX_FOLLOW_UP_PROMPTS[3]
    );
    if (!optimizationInFlight) {
      options.push(SANDBOX_FOLLOW_UP_PROMPTS[4]);
      options.push(SANDBOX_FOLLOW_UP_PROMPTS[5]);
    }
  } else {
    options.push(
      SANDBOX_FOLLOW_UP_PROMPTS[0],
      SANDBOX_FOLLOW_UP_PROMPTS[1],
      SANDBOX_FOLLOW_UP_PROMPTS[2]
    );
    if (!hasStandards && !optimizationInFlight) {
      options.push({
        label: "Recommend standards",
        prompt: SANDBOX_INITIAL_PROMPTS[7],
      });
    }
  }

  const unique = [];
  options.forEach((option) => {
    if (!option.prompt) return;
    if (!unique.some((existing) => existing.prompt === option.prompt)) {
      unique.push(option);
    }
  });
  return unique.slice(0, 7);
}

const assistantMarkdownComponents = {
  h2: ({ children }) => (
    <h3 className="mb-1.5 mt-3 first:mt-0 text-sm font-semibold text-slate-950">
      {children}
    </h3>
  ),
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
};

function AssistantMarkdown({ content }) {
  return (
    <ReactMarkdown components={assistantMarkdownComponents}>
      {content}
    </ReactMarkdown>
  );
}

function AssistantMessage({ message, messageRef }) {
  const isUser = message.role === "user";
  const [expanded, setExpanded] = React.useState(false);
  const content = String(message.content || "");
  const configurationMarker = "## Configuration details";
  const configurationStart = content.indexOf(configurationMarker);
  const hasConfigurationDetails = !isUser && configurationStart >= 0;
  const summaryContent = hasConfigurationDetails
    ? content.slice(0, configurationStart).trim()
    : content;
  const configurationContent = hasConfigurationDetails
    ? content.slice(configurationStart).trim()
    : "";
  const canCollapse =
    !isUser && (hasConfigurationDetails || content.length > 1100);
  return (
    <div
      ref={messageRef}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        aria-label={isUser ? "You" : "Labor Model Assistant"}
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-6 ${
          isUser
            ? "bg-violet-100 text-gray-700"
            : "border border-gray-200 bg-white text-gray-700"
        }`}
      >
        {isUser ? (
          content
        ) : (
          <>
            <div
              className={`relative ${
                canCollapse && !hasConfigurationDetails && !expanded
                  ? "max-h-64 overflow-hidden"
                  : ""
              }`}
            >
              <AssistantMarkdown content={summaryContent} />
              {hasConfigurationDetails && expanded ? (
                <AssistantMarkdown content={configurationContent} />
              ) : null}
              {canCollapse && !hasConfigurationDetails && !expanded ? (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent" />
              ) : null}
            </div>
            {canCollapse ? (
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((current) => !current)}
                className="mt-2 text-xs font-semibold text-violet-700 hover:text-violet-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
              >
                {expanded
                  ? "Hide configuration details"
                  : "View configuration details"}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function ProposalCard({ proposal, resolvingAction, disabled, onApply, onDiscard }) {
  if (!proposal) return null;
  const isOptimizerRun = proposal.kind === "optimizer_run";
  const isOptimizerApply = proposal.kind === "optimizer_apply";
  const applyLabel = isOptimizerRun
    ? "Refit this labor model"
    : isOptimizerApply
      ? "Apply to sandbox"
      : "Apply to sandbox";
  return (
    <section
      aria-labelledby="labor-model-assistant-proposal-title"
      className="rounded-xl border border-violet-200 bg-violet-50/50 p-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
          <TuneOutlinedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
        </span>
        <div className="min-w-0 flex-1">
          <p
            id="labor-model-assistant-proposal-title"
            className="text-sm font-semibold text-slate-950"
          >
            {proposal.summary}
          </p>
          {proposal.rationale ? (
            <p className="mt-1 text-xs leading-5 text-slate-600">
              {proposal.rationale}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {(proposal.changes || []).map((change, index) => (
          <div
            key={`${change.title}-${index}`}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
          >
            <div className="flex items-start gap-2">
              <span
                className={`mt-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  sectionTone[change.change_type] ||
                  "bg-slate-100 text-slate-600"
                }`}
              >
                {change.change_type}
              </span>
              <div className="min-w-0">
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {proposalSectionLabel(change)}
                </p>
                <p className="text-xs font-semibold text-slate-800">
                  {change.title}
                </p>
                {(change.details || []).slice(0, 4).map((detail) => {
                  const parsedDetail = parseProposalDetail(detail);
                  return parsedDetail ? (
                    <div
                      key={detail}
                      className="mt-2 rounded-md bg-slate-50 px-2.5 py-2"
                    >
                      <p className="text-[10px] font-medium text-slate-500">
                        {parsedDetail.label}
                      </p>
                      <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-xs">
                        <span className="truncate text-slate-500 line-through">
                          {parsedDetail.previous}
                        </span>
                        <span aria-hidden="true" className="text-slate-400">
                          →
                        </span>
                        <span className="truncate font-semibold text-slate-900">
                          {parsedDetail.next}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p key={detail} className="mt-1 text-[11px] text-slate-500">
                      {detail}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-800">
        {isOptimizerRun
          ? "This starts a background calculation only. The sandbox will not change until you separately review and approve the result."
          : "Applying this change will make Review Labor Plan results out of date. Refresh those results before publishing."}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Applies only to this sandbox. Published models are not changed.
      </p>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={onDiscard}
          className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-semibold text-slate-600 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:opacity-50"
        >
          {resolvingAction === "discard" ? "Discarding…" : "Discard"}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={onApply}
          className="inline-flex h-9 items-center rounded-lg bg-violet-700 px-4 text-sm font-semibold text-white hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 disabled:bg-violet-400"
        >
          {resolvingAction === "apply"
            ? isOptimizerRun
              ? "Starting…"
              : "Applying…"
            : applyLabel}
        </button>
      </div>
    </section>
  );
}

function LaborModelGuidedSetup({
  companyId,
  scenario,
  publication,
  readOnly = false,
  connected = false,
  modal = false,
  onClose,
  setupSummary,
  evaluation,
  evaluationFresh,
  laborPlanSummary,
  scenarioChangeCount,
  scenarioChangeCountLoading,
  onScenarioApplied,
}) {
  const [sessionUuid, setSessionUuid] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [proposal, setProposal] = React.useState(null);
  const [optimizationRun, setOptimizationRun] = React.useState(null);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [resolvingAction, setResolvingAction] = React.useState(null);
  const [resetting, setResetting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [showJumpToLatest, setShowJumpToLatest] = React.useState(false);
  const messagesContainerRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const messageRefs = React.useRef(new Map());
  const pendingAssistantScrollRef = React.useRef(false);
  const activeScopeRef = React.useRef("");
  const selectedModel = readOnly ? publication : scenario;
  const selectedModelUuid = selectedModel?.uuid;
  const selectedModelRevision = readOnly
    ? selectedModel?.version
    : selectedModel?.revision;
  const evaluationContext = React.useMemo(
    () =>
      evaluationFresh
        ? buildLaborModelAssistantEvaluationContext(
            evaluation,
            laborPlanSummary
          )
        : null,
    [evaluation, evaluationFresh, laborPlanSummary]
  );

  const initialActionPills = React.useMemo(
    () =>
      buildInitialActionPills({
        readOnly,
        setupSummary,
        scenarioChangeCount,
        scenarioChangeCountLoading,
        evaluationFresh,
        optimizationRun,
        messagesCount: messages.length,
    }),
    [
      messages.length,
      optimizationRun,
      evaluationFresh,
      readOnly,
      scenarioChangeCount,
      scenarioChangeCountLoading,
      setupSummary,
    ]
  );

  const followUpActionPills = React.useMemo(
    () =>
      buildFollowUpActionPills({
        readOnly,
        scenarioChangeCount,
        setupSummary,
        optimizationRun,
        evaluationFresh,
        proposal,
        messages,
      }),
    [
      evaluationFresh,
      messages,
      optimizationRun,
      proposal,
      readOnly,
      scenarioChangeCount,
      setupSummary,
    ]
  );

  React.useEffect(() => {
    if (!selectedModelUuid) return undefined;
    let current = true;
    const scope = `${companyId}:${readOnly ? "publication" : "scenario"}:${selectedModelUuid}:${selectedModelRevision}`;
    activeScopeRef.current = scope;
    setLoading(true);
    setError("");
    setSessionUuid(null);
    setMessages([]);
    setProposal(null);
    setInput("");
    setSending(false);
    setResolvingAction(null);
    setResetting(false);
    if (readOnly) {
      setLoading(false);
      return () => {
        current = false;
      };
    }
    getRightworkAPI(API_get_labor_model_assistant_session, {
      company_id: companyId,
      scenario_uuid: scenario.uuid,
    })
      .then((response) => {
        if (!current || activeScopeRef.current !== scope) return;
        const session = response?.data?.redux?.payload?.session || {};
        setSessionUuid(session.uuid || null);
        setMessages(session.messages || []);
        setProposal(session.pending_change_set || null);
      })
      .catch((requestError) => {
        if (!current || activeScopeRef.current !== scope) return;
        setError(
          requestError.response?.data?.system?.message ||
            "Unable to load the Labor Model Assistant."
        );
      })
      .finally(() => {
        if (current && activeScopeRef.current === scope) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [
    companyId,
    readOnly,
    scenario?.uuid,
    selectedModelRevision,
    selectedModelUuid,
  ]);

  React.useEffect(() => {
    if (readOnly || !scenario?.uuid) {
      setOptimizationRun(null);
      return undefined;
    }
    let current = true;
    let timeoutId = null;

    const loadOptimizationRun = async () => {
      try {
        const response = await getRightworkAPI(
          API_get_labor_standard_optimization_runs,
          {
            company_id: companyId,
            scenario_uuid: scenario.uuid,
            scenario_revision: scenario.revision,
          }
        );
        if (!current) return;
        const latestRun =
          response?.data?.redux?.payload?.latest_optimization_run || null;
        setOptimizationRun(latestRun);
        if (["queued", "running"].includes(latestRun?.status)) {
          timeoutId = window.setTimeout(loadOptimizationRun, 4000);
        }
      } catch (requestError) {
        if (current) setOptimizationRun(null);
      }
    };

    loadOptimizationRun();
    return () => {
      current = false;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [
    companyId,
    readOnly,
    scenario?.uuid,
    scenario?.revision,
    optimizationRun?.status,
  ]);

  const scrollToLatest = React.useCallback((behavior = "smooth") => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  React.useEffect(() => {
    if (!pendingAssistantScrollRef.current || messages.length === 0) return;
    const lastIndex = messages.length - 1;
    if (messages[lastIndex]?.role !== "assistant") return;
    const container = messagesContainerRef.current;
    const target = messageRefs.current.get(lastIndex);
    if (!container || !target) return;
    pendingAssistantScrollRef.current = false;
    const containerTop = container.getBoundingClientRect().top;
    const targetTop = target.getBoundingClientRect().top;
    container.scrollTo({
      top: Math.max(0, container.scrollTop + targetTop - containerTop - 8),
      behavior: "smooth",
    });
  }, [messages]);

  React.useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [input]);

  const handleConversationScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowJumpToLatest(distanceFromBottom > 96);
  };

  const sendMessage = async (suggestedPrompt) => {
    const query = String(suggestedPrompt || input).trim();
    if (!query || sending || resolvingAction || resetting) return;
    const requestScope = activeScopeRef.current;
    setInput("");
    setError("");
    setSending(true);
    setMessages((current) => [
      ...current,
      { role: "user", content: query },
    ]);
    window.requestAnimationFrame(() => scrollToLatest());
    try {
      const response = await postRightworkAPI(API_labor_model_assistant_chat, {
        company_id: companyId,
        ...(readOnly
          ? {
              publication_uuid: publication.uuid,
              conversation_history: messages,
            }
          : {
              scenario_uuid: scenario.uuid,
              expected_revision: scenario.revision,
              session_uuid: sessionUuid,
            }),
        user_query: query,
        evaluation_context: evaluationContext,
      });
      const payload = response?.data?.redux?.payload || {};
      if (activeScopeRef.current !== requestScope) return;
      if (!readOnly) setSessionUuid(payload.session_uuid || sessionUuid);
      pendingAssistantScrollRef.current = true;
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: payload.assistant_message || "I completed the review.",
        },
      ]);
      setProposal(readOnly ? null : payload.proposal || null);
    } catch (requestError) {
      if (activeScopeRef.current !== requestScope) return;
      setError(
        requestError.response?.data?.system?.message ||
          "The assistant could not complete that request."
      );
    } finally {
      if (activeScopeRef.current === requestScope) setSending(false);
    }
  };

  const resolveProposal = async (action) => {
    if (readOnly || !proposal || resolvingAction || sending || resetting) return;
    const requestScope = activeScopeRef.current;
    setResolvingAction(action);
    setError("");
    try {
      const response = await postRightworkAPI(
        API_resolve_labor_model_assistant_proposal,
        {
          company_id: companyId,
          scenario_uuid: scenario.uuid,
          expected_revision: scenario.revision,
          session_uuid: sessionUuid,
          proposal_id: proposal.proposal_id,
          action,
        }
      );
      const payload = response?.data?.redux?.payload || {};
      if (activeScopeRef.current !== requestScope) return;
      setProposal(payload.proposal || null);
      if (action === "apply" && payload.scenario) {
        pendingAssistantScrollRef.current = true;
        setMessages((current) => [
          ...current,
          {
            role: "assistant",
            content:
              "The approved changes were applied to this sandbox. Review Labor Plan now needs a refresh before publishing.",
          },
        ]);
        if (payload.optimization_run) {
          setOptimizationRun(payload.optimization_run);
        }
        onScenarioApplied(payload.scenario);
      } else if (action === "apply" && payload.optimization_run) {
        setOptimizationRun(payload.optimization_run);
        pendingAssistantScrollRef.current = true;
        setMessages((current) => [
          ...current,
          {
            role: "assistant",
            content:
              "The recommendations are being generated in the background. This sandbox will not change until you review and approve them.",
          },
        ]);
      } else {
        pendingAssistantScrollRef.current = true;
        setMessages((current) => [
          ...current,
          { role: "assistant", content: "The proposed changes were discarded." },
        ]);
      }
    } catch (requestError) {
      if (activeScopeRef.current !== requestScope) return;
      setError(
        requestError.response?.data?.system?.message ||
          "Unable to update the proposal."
      );
    } finally {
      if (activeScopeRef.current === requestScope) setResolvingAction(null);
    }
  };

  const startNewConversation = async () => {
    if (loading || sending || resolvingAction || resetting) return;
    if (
      proposal &&
      !window.confirm(
        "Start a new conversation? This will discard the unconfirmed proposal. Your labor model will not change."
      )
    ) {
      return;
    }
    const requestScope = activeScopeRef.current;
    setError("");
    setResetting(true);
    try {
      if (!readOnly) {
        const response = await postRightworkAPI(
          API_reset_labor_model_assistant_session,
          {
            company_id: companyId,
            scenario_uuid: scenario.uuid,
            expected_revision: scenario.revision,
            session_uuid: sessionUuid,
          }
        );
        if (activeScopeRef.current !== requestScope) return;
        const session = response?.data?.redux?.payload?.session || {};
        setSessionUuid(session.uuid || sessionUuid);
      }
      if (activeScopeRef.current !== requestScope) return;
      pendingAssistantScrollRef.current = false;
      messageRefs.current.clear();
      setMessages([]);
      setProposal(null);
      setInput("");
      setShowJumpToLatest(false);
      window.requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (requestError) {
      if (activeScopeRef.current !== requestScope) return;
      setError(
        requestError.response?.data?.system?.message ||
          "Unable to start a new conversation."
      );
    } finally {
      if (activeScopeRef.current === requestScope) setResetting(false);
    }
  };

  return (
    <section
      aria-label="Labor Model Assistant"
      className={`relative flex flex-col overflow-hidden bg-gray-50 ${
        modal
          ? "h-full min-h-0 max-h-none"
          : "h-[calc(100vh-180px)] min-h-[640px] max-h-[860px] lg:h-[calc(100vh-160px)] lg:min-h-[620px] lg:max-h-none"
      } ${
        connected ? "" : "rounded-xl border border-slate-200"
      }`}
    >
      <header className="shrink-0 border-b border-gray-200 bg-white px-5 py-3 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
              <ChatOutlinedIcon aria-hidden="true" sx={{ fontSize: 19 }} />
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <h2
                id="labor-model-assistant-title"
                className="text-sm font-medium text-gray-800"
              >
                Chat about labor model
              </h2>
              <p className="text-xs text-gray-500">
                {readOnly
                  ? "Ask questions about this published model"
                  : "Ask questions or prepare sandbox changes"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={startNewConversation}
              disabled={
                loading ||
                sending ||
                Boolean(resolvingAction) ||
                resetting ||
                (messages.length === 0 && !proposal && !input.trim())
              }
              className="px-1 text-xs text-gray-500 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {resetting ? "Resetting…" : "Reset"}
            </button>
            {onClose ? (
              <button
                type="button"
                aria-label="Close labor model assistant"
                onClick={onClose}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
              >
                <CloseIcon aria-hidden="true" sx={{ fontSize: 19 }} />
              </button>
            ) : null}
          </div>
        </div>
      </header>

        <div
          ref={messagesContainerRef}
          onScroll={handleConversationScroll}
          aria-busy={loading || sending || resetting}
          aria-live="polite"
          className="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-3"
        >
          {!loading && messages.length === 0 ? (
            <div className="max-w-4xl">
              <h3 className="text-base font-semibold text-gray-800">
                {readOnly
                  ? "What would you like to understand?"
                  : "What would you like to improve?"}
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                {readOnly
                  ? "I can explain this model's configuration, calculated results, standards, drivers, and store-level impact. Create a sandbox if you want to make changes."
                  : "I can explain how the model works, answer questions about its calculated results, prepare changes for approval, or recommend labor standards from historical data."}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {initialActionPills.map((pill) => (
                  <button
                    key={pill.prompt}
                    type="button"
                    onClick={() => sendMessage(pill.prompt)}
                    disabled={
                      loading ||
                      sending ||
                      Boolean(resolvingAction) ||
                      resetting ||
                      Boolean(pill.disabledReason)
                    }
                    title={pill.disabledReason || undefined}
                    className={`rounded-full border border-violet-300 bg-white px-3 py-1.5 text-left text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:cursor-not-allowed disabled:opacity-45 ${
                      pill.disabledReason
                        ? "text-slate-400"
                        : "text-violet-700 hover:bg-violet-50"
                    }`}
                  >
                    {pill.label || pill.prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center text-sm font-medium text-slate-500">
              Loading guided setup…
            </div>
          ) : null}
          {messages.map((message, index) => (
            <AssistantMessage
              key={`${message.role}-${index}`}
              message={message}
              messageRef={(element) => {
                if (element) messageRefs.current.set(index, element);
                else messageRefs.current.delete(index);
              }}
            />
          ))}
          {sending ? (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-700" />
                Reviewing the model…
              </div>
            </div>
          ) : null}
          <ProposalCard
            proposal={readOnly ? null : proposal}
            resolvingAction={resolvingAction}
            disabled={Boolean(resolvingAction) || sending || resetting}
            onApply={() => resolveProposal("apply")}
            onDiscard={() => resolveProposal("discard")}
          />
          {error ? (
            <div
              role="alert"
              className="flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700"
            >
              <span className="inline-flex items-start gap-2">
                <WarningAmberOutlinedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 17 }}
                />
                {error}
              </span>
              <button
                type="button"
                aria-label="Dismiss assistant error"
                onClick={() => setError("")}
                className="rounded text-rose-600 hover:text-rose-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
              >
                <CloseIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              </button>
            </div>
          ) : null}
          {messages.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {followUpActionPills.map(({ label, prompt, disabledReason }) => (
                <button
                  key={`${label}-${prompt}`}
                  type="button"
                  disabled={
                    loading ||
                    sending ||
                    Boolean(resolvingAction) ||
                    resetting ||
                    Boolean(proposal) ||
                    Boolean(disabledReason)
                  }
                  title={disabledReason || undefined}
                  onClick={() => sendMessage(prompt)}
                  className={`rounded-full border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:cursor-not-allowed disabled:opacity-50 ${
                    disabledReason
                      ? "text-slate-400"
                      : "text-violet-700 hover:bg-violet-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {showJumpToLatest ? (
          <button
            type="button"
            onClick={() => scrollToLatest()}
            className="absolute bottom-[68px] right-4 z-10 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
          >
            Jump to latest
          </button>
        ) : null}

        <form
          className="shrink-0 border-t border-gray-200 bg-white p-2"
          onSubmit={(event) => {
            event.preventDefault();
            sendMessage();
          }}
        >
          <label className="sr-only" htmlFor="labor-model-assistant-input">
            Ask the Labor Model Assistant
          </label>
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              id="labor-model-assistant-input"
              rows="1"
              value={input}
              disabled={
                loading || sending || Boolean(resolvingAction) || resetting
              }
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={
                readOnly
                  ? "Ask a question about this published model…"
                  : "Ask a question or describe what you want to change…"
              }
              className="flex-1 resize-none rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 outline-none placeholder:text-gray-400 disabled:opacity-60"
              style={{ maxHeight: 100 }}
            />
            <button
              type="submit"
              disabled={
                !input.trim() ||
                loading ||
                sending ||
                Boolean(resolvingAction) ||
                resetting
              }
              aria-label="Send message"
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                input.trim()
                  ? "bg-violet-700 text-white"
                  : "bg-gray-200 text-gray-400"
              }`}
            >
              <SendIcon aria-hidden="true" fontSize="small" />
            </button>
          </div>
        </form>
    </section>
  );
}

export default LaborModelGuidedSetup;
